import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, ChatCircleDots, Phone, ArrowRight, ArrowLeft, UserCircle, Robot, ArrowsClockwise, Clock } from "@phosphor-icons/react";
import { useGeneralSettings } from "@/hooks/useGeneralSettings";

function slaLabel(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMin = Math.max(0, Math.floor((now - then) / 60000));
  if (diffMin < 60) return { text: `${diffMin} min`, warn: diffMin >= 15, danger: diffMin >= 30 };
  const h = Math.floor(diffMin / 60);
  if (h < 24) return { text: `${h}h`, warn: true, danger: true };
  return { text: `${Math.floor(h / 24)}d`, warn: true, danger: true };
}

const COLS = [
  { key: "aguardando", label: "Aguardando", color: "#D97706" },
  { key: "em_atendimento", label: "Em Atendimento", color: "#2563EB" },
  { key: "resolvido", label: "Resolvido", color: "#0F4C3A" },
];

const emptyTicket = { contact_name: "", contact_phone: "", subject: "" };

export default function CompanyKanban() {
  const [tickets, setTickets] = useState([]);
  const [attendants, setAttendants] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyTicket);
  const [assignTicket, setAssignTicket] = useState(null);
  const [assignForm, setAssignForm] = useState({ assigned_to: "", sector_id: "" });
  const [filterSector, setFilterSector] = useState("all");
  const [filterAttendant, setFilterAttendant] = useState("all");
  const navigate = useNavigate();
  const { maskPhone, displayContactName } = useGeneralSettings();

  const load = async () => {
    const [t, a, s] = await Promise.all([
      api.get("/tickets"),
      api.get("/company/attendants").catch(() => ({ data: [] })),
      api.get("/company/sectors").catch(() => ({ data: [] })),
    ]);
    setTickets(t.data);
    setAttendants(a.data);
    setSectors(s.data);
  };
  useEffect(() => {
    load();
    const interval = setInterval(() => api.get("/tickets").then((r) => setTickets(r.data)), 5000);
    return () => clearInterval(interval);
  }, []);

  const create = async () => {
    try {
      await api.post("/tickets", { ...form, status: "aguardando" });
      toast.success("Ticket criado");
      setOpen(false);
      setForm(emptyTicket);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const move = async (t, direction) => {
    const idx = COLS.findIndex((c) => c.key === t.status);
    const next = COLS[idx + direction];
    if (!next) return;
    try {
      await api.patch(`/tickets/${t.id}/status`, { status: next.key });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openAssign = (t) => {
    setAssignTicket(t);
    setAssignForm({ assigned_to: t.assigned_to || "", sector_id: t.sector_id || "" });
  };
  const saveAssign = async () => {
    try {
      await api.patch(`/tickets/${assignTicket.id}/assign`, {
        assigned_to: assignForm.assigned_to || null,
        sector_id: assignForm.sector_id || null,
      });
      toast.success("Ticket atualizado");
      setAssignTicket(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleAi = async (t) => {
    try {
      await api.patch(`/tickets/${t.id}/ai`, { ai_enabled: t.ai_enabled === false ? true : false });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const filtered = tickets.filter((t) => {
    if (filterSector !== "all" && t.sector_id !== filterSector) return false;
    if (filterAttendant !== "all" && t.assigned_to !== filterAttendant) return false;
    return true;
  });

  const grouped = COLS.reduce((acc, c) => {
    acc[c.key] = filtered.filter((t) => t.status === c.key);
    return acc;
  }, {});

  return (
    <div className="p-8">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#0F4C3A]">Atendimento</p>
          <h1 className="text-4xl font-black tracking-tighter text-[#1A1A1A] mt-1">Kanban</h1>
          <p className="text-sm text-[#5C5C59] mt-2">Organize seus tickets do WhatsApp em fluxo visual.</p>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest font-semibold">Setor</Label>
            <Select value={filterSector} onValueChange={setFilterSector}>
              <SelectTrigger data-testid="filter-sector" className="rounded-none mt-1 h-9 min-w-[140px] bg-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white rounded-none">
                <SelectItem value="all">Todos setores</SelectItem>
                {sectors.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest font-semibold">Atendente</Label>
            <Select value={filterAttendant} onValueChange={setFilterAttendant}>
              <SelectTrigger data-testid="filter-attendant" className="rounded-none mt-1 h-9 min-w-[160px] bg-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white rounded-none">
                <SelectItem value="all">Todos atendentes</SelectItem>
                {attendants.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-ticket-btn" className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none shadow-brutal transition-colors duration-200 h-9">
                <Plus size={16} weight="bold" className="mr-1.5" /> Novo ticket
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white rounded-none border-[#E5E5E0]">
              <DialogHeader><DialogTitle>Novo ticket</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs uppercase tracking-wider font-semibold">Nome do contato</Label>
                  <Input data-testid="ticket-contact-name-input" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="rounded-none mt-1" /></div>
                <div><Label className="text-xs uppercase tracking-wider font-semibold">Telefone</Label>
                  <Input data-testid="ticket-contact-phone-input" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="+55 11 99999-9999" className="rounded-none mt-1" /></div>
                <div><Label className="text-xs uppercase tracking-wider font-semibold">Assunto</Label>
                  <Input data-testid="ticket-subject-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="rounded-none mt-1" /></div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-none">Cancelar</Button>
                <Button data-testid="ticket-save-btn" onClick={create} className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none">Criar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="kanban-board">
        {COLS.map((col) => (
          <div key={col.key} data-testid={`kanban-col-${col.key}`}>
            <div className="bg-white border border-[#E5E5E0] shadow-brutal">
              <div className="border-t-[4px] px-4 py-3 flex items-center justify-between" style={{ borderTopColor: col.color }}>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#5C5C59]">Coluna</div>
                  <h3 className="text-lg font-bold tracking-tight">{col.label}</h3>
                </div>
                <div className="font-mono text-2xl font-black" style={{ color: col.color }}>
                  {String(grouped[col.key].length).padStart(2, "0")}
                </div>
              </div>
              <div className="p-3 space-y-3 min-h-[400px] max-h-[calc(100vh-260px)] overflow-y-auto">
                {grouped[col.key].length === 0 && (
                  <div className="text-xs text-[#5C5C59] text-center py-8">Nenhum ticket aqui</div>
                )}
                {grouped[col.key].map((t) => {
                  const colIdx = COLS.findIndex((c) => c.key === t.status);
                  const sector = sectors.find((s) => s.id === t.sector_id);
                  return (
                    <div key={t.id} data-testid={`ticket-card-${t.id}`}
                      className="bg-[#F7F7F5] border border-[#E5E5E0] p-3 hover:-translate-y-0.5 transition-transform duration-200 shadow-brutal-hover">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="h-8 w-8 bg-[#0F4C3A] text-white flex items-center justify-center text-xs font-bold">
                          {t.contact_name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold truncate">{displayContactName(t.contact_name, t.contact_phone)}</div>
                          <div className="flex items-center gap-1 text-[10px] text-[#5C5C59] font-mono">
                            <Phone size={10} /> {maskPhone(t.contact_phone)}
                          </div>
                        </div>
                      </div>
                      <div className="text-xs text-[#1A1A1A] mb-2">{t.subject}</div>
                      {t.last_message && (
                        <div className="text-[11px] text-[#5C5C59] italic truncate border-l-2 border-[#E5E5E0] pl-2 mb-2">
                          "{t.last_message}"
                        </div>
                      )}
                      {/* Assignment info */}
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        {sector && (
                          <span className="text-[9px] font-semibold uppercase tracking-widest px-1.5 py-0.5" style={{ background: sector.color + "22", color: sector.color }}>
                            {sector.name}
                          </span>
                        )}
                        {t.assigned_to_name ? (
                          <span className="text-[10px] flex items-center gap-1 text-[#1A1A1A]">
                            <UserCircle size={12} weight="fill" className="text-[#0F4C3A]" />
                            {t.assigned_to_name}
                          </span>
                        ) : (
                          <span className="text-[10px] text-[#5C5C59]">Sem atendente</span>
                        )}
                        {t.ai_enabled !== false && (
                          <span className="text-[9px] font-semibold uppercase tracking-widest px-1.5 py-0.5 bg-[#EDF3F0] text-[#0F4C3A] flex items-center gap-1">
                            <Robot size={10} weight="fill" /> IA
                          </span>
                        )}
                        {/* SLA */}
                        {t.status !== "resolvido" && t.last_customer_message_at && !t.first_response_at && (() => {
                          const sla = slaLabel(t.last_customer_message_at);
                          if (!sla) return null;
                          const color = sla.danger ? "#DC2626" : sla.warn ? "#D97706" : "#0F4C3A";
                          return (
                            <span data-testid={`sla-${t.id}`} className="text-[9px] font-mono font-bold uppercase tracking-widest px-1.5 py-0.5 flex items-center gap-1" style={{ background: color + "22", color }}>
                              <Clock size={10} weight="fill" /> {sla.text}
                            </span>
                          );
                        })()}
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t border-[#E5E5E0]">
                        <div className="flex gap-1">
                          <button onClick={() => navigate(`/chat/${t.id}`)} data-testid={`open-chat-${t.id}`}
                            className="flex items-center gap-1 text-xs font-semibold text-[#0F4C3A] hover:underline">
                            <ChatCircleDots size={14} weight="bold" /> Chat
                          </button>
                          <button onClick={() => openAssign(t)} data-testid={`assign-${t.id}`}
                            className="flex items-center gap-1 text-xs font-semibold text-[#5C5C59] hover:text-[#0F4C3A] ml-3">
                            <ArrowsClockwise size={12} weight="bold" /> Transferir
                          </button>
                          <button onClick={() => toggleAi(t)} data-testid={`toggle-ai-${t.id}`}
                            className={`flex items-center gap-1 text-xs font-semibold ml-3 ${t.ai_enabled === false ? "text-[#5C5C59]" : "text-[#0F4C3A]"} hover:opacity-70`}
                            title={t.ai_enabled === false ? "IA desativada" : "IA ativa"}>
                            <Robot size={12} weight={t.ai_enabled === false ? "regular" : "fill"} />
                          </button>
                        </div>
                        <div className="flex gap-1">
                          {colIdx > 0 && <button onClick={() => move(t, -1)} data-testid={`move-back-${t.id}`} className="p-1 hover:bg-white transition-colors"><ArrowLeft size={14} /></button>}
                          {colIdx < COLS.length - 1 && <button onClick={() => move(t, 1)} data-testid={`move-forward-${t.id}`} className="p-1 hover:bg-white transition-colors"><ArrowRight size={14} /></button>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Assign modal */}
      <Dialog open={!!assignTicket} onOpenChange={(v) => !v && setAssignTicket(null)}>
        <DialogContent className="bg-white rounded-none border-[#E5E5E0]">
          <DialogHeader><DialogTitle>Transferir ticket</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Atendente</Label>
              <Select value={assignForm.assigned_to || "__none__"} onValueChange={(v) => setAssignForm({ ...assignForm, assigned_to: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="assign-attendant-select" className="rounded-none mt-1 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white rounded-none">
                  <SelectItem value="__none__">Sem atendente</SelectItem>
                  {attendants.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Setor</Label>
              <Select value={assignForm.sector_id || "__none__"} onValueChange={(v) => setAssignForm({ ...assignForm, sector_id: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="assign-sector-select" className="rounded-none mt-1 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white rounded-none">
                  <SelectItem value="__none__">Sem setor</SelectItem>
                  {sectors.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAssignTicket(null)} className="rounded-none">Cancelar</Button>
            <Button data-testid="assign-save-btn" onClick={saveAssign} className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
